/**
 * Breakdown menu: multi-screen menu to select event+prop pair
 */
import {customEventsStore} from 'common/report/stores/custom-events';
import {renameEvent} from 'common/report/util/events';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {propagateEvent} from 'common/util/dom';
import {eventDefinitionsStore} from 'common/report/stores/event-definitions';

import {getCustomEventItemsFiltered} from '../util';
import {ChildMenu} from '../child-menu';
import {EventItem, EventsMenu} from '../events-menu/types';
import {IPropertiesMenu} from '../properties-menu/types';
import template from './index.jade';

interface BreakdownMenuState {
  eventData: EventItem[];
  staged: {
    event: CustomEvent;
  };
}

export class BreakdownMenu extends ChildMenu<BreakdownMenuState> {
  private childMenu: EventsMenu | IPropertiesMenu;
  private customEventsStoreCallback: () => void;

  static get attrsSchema() {
    return {
      ...super.attrsSchema,
      'include-custom-properties': {
        type: `string`,
        enum: [`none`, `persisted-only`, `persisted-non-behavioral-only`, `all`],
        default: `none`,
        description: `whether to hide custom properties from properties menus`,
      },
    };
  }

  get config() {
    return {
      template,

      defaultState: {
        eventData: [],
        staged: null,
      },

      helpers: {
        ...super.config.helpers,
        onMenuInserted: (vnode) => requestAnimationFrame(() => (this.childMenu = vnode.elm)),
        getSelectedEvent: () => this.getSelectedEvent(),
        getSelectedProperty: () => this.attr(`selected`)?.property || null,
        handleSelectEvent: (ev) => {
          this.update({staged: {event: ev.detail.item}, searchFilter: ``});
        },
        propagateEvent: (ev) => propagateEvent(this, ev),
        handleSelectProperty: (ev) => {
          const property = ev.detail.item;
          this.dispatchEvent(
            new CustomEvent(`select`, {
              detail: {
                item: {
                  event: this.getSelectedEvent(),
                  property,
                },
              },
            }),
          );
        },
        /** Since mp-properties-menu defines when content is searchable, trigger a manual render */
        handleStagedProperty: () => this.update(),
      },
    };
  }

  connectedCallback() {
    super.connectedCallback();
    this.customEventsStoreCallback = () => this.updateEventItems();
    customEventsStore.subscribe(this.customEventsStoreCallback);
  }

  disconnectedCallback() {
    customEventsStore.unsubscribe(this.customEventsStoreCallback);
    this.customEventsStoreCallback = null;
    super.disconnectedCallback();
  }

  public isContentSearchable() {
    return this.childMenu.isContentSearchable();
  }

  selectedLabel() {
    const evName = renameEvent(this.getSelectedEventName());
    if (evName) {
      return [evName, `by`, this.getSelectedPropertyName() || `...`].join(` `);
    } else {
      return `Select event...`;
    }
  }

  // we need to get the custom event items for flows expansion because event-menu is nested
  // in breakown-menu, and so when we want to edit custom events we need eventData to be populated
  // with custom events in order to use it
  getCustomEventItems(): EventItem[] {
    if (this.attr(`exclude-custom-events`)) {
      return [];
    }

    return getCustomEventItemsFiltered({
      customEventsStoreValues: customEventsStore.value,
      eventDefinitionsStoreValues: eventDefinitionsStore.value,
      excludeAutotrackEvents: this.attr(`exclude-autotrack-events`),
      dataGroupId: this.attr(`data-group-id`),
      eventData: this.state.eventData,
    });
  }

  updateEventItems() {
    const items = this.getCustomEventItems();

    this.update({
      eventData: items,
    });
  }
}

export const BreakdownMenuEl = defineAndMakeJsxFactory(`mp-breakdown-menu`, BreakdownMenu);
